import { useEffect, useRef } from 'react'
import { sessionViewModeState } from '../../../shared/useViewMode'

export function useVideoGridContainerRef(
    numVids: number,
    totalVids: number,
    isSidebarRetracted: boolean,
    fullscreen: boolean
) {
    const container = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (!container.current) {
            return
        }
        let videoCountMaxColumns = 1
        if (totalVids >= 10) {
            videoCountMaxColumns = 4
        } else if (totalVids >= 5) {
            videoCountMaxColumns = 3
        } else if (totalVids >= 2) {
            videoCountMaxColumns = 2
        }

        const calcFlexBasisVal = (sizeMaxCols: number, vidCountMaxCols: number) => {
            if (sizeMaxCols === 2 && totalVids === 2) {
                // Special case for 2 videos on sm screen, switch to vertical
                // layout sooner to maintain larger video boxes.
                return '70%'
            }
            // the `reservedWidth` is a hueristic to avoid scroll for common
            // screen sizes with a small number of participants. When the
            // sidebar is hidden videos get more horizontal space, which causes
            // the grid to grow vertically since aspect ration is preserved.
            const reservedWidth = isSidebarRetracted ? 10 : 5
            const cols = Math.min(sizeMaxCols, vidCountMaxCols)

            // special case to fit a  4x4 grid
            if (cols === 4 && numVids > 12 && numVids <= 16) {
                return '24%'
            }

            const perc = (1 / cols) * 100 - reservedWidth / cols
            return `${perc.toFixed(0)}%`
        }

        const calcMaxHeightVal = (sizeMaxCols: number, vidCountMaxCols: number) => {
            const cols = Math.min(sizeMaxCols, vidCountMaxCols)
            const rows = Math.ceil(numVids / cols)

            // Special case for 2 videos on sm screen, switch to vertical
            // layout sooner to maintain larger video boxes.
            if (sizeMaxCols === 2 && numVids === 2) {
                return '50%'
            }

            // special case to avoid scrolling with small sessions
            if (cols > 1 && rows <= 3) {
                return `${(100 / rows).toFixed(0)}%`
            }

            // special case to fit a  4x4 grid
            if (cols === 4 && numVids > 12 && numVids <= 16) {
                return '25%'
            }
            return '100%'
        }

        container.current.style.setProperty('--lg-columns', calcFlexBasisVal(4, videoCountMaxColumns))
        container.current.style.setProperty('--md-columns', calcFlexBasisVal(3, videoCountMaxColumns))
        container.current.style.setProperty('--sm-columns', calcFlexBasisVal(2, videoCountMaxColumns))

        container.current.style.setProperty('--lg-vid-max-h', calcMaxHeightVal(4, videoCountMaxColumns))
        container.current.style.setProperty('--md-vid-max-h', calcMaxHeightVal(3, videoCountMaxColumns))
        container.current.style.setProperty('--sm-vid-max-h', calcMaxHeightVal(2, videoCountMaxColumns))

        const sessionPageEl = document.getElementById('session-page')
        const checkScroll = () => {
            if (container.current) {
                if (numVids > 1) {
                    const hasScroll = container.current.scrollHeight > container.current.clientHeight
                    if (hasScroll) {
                        sessionPageEl?.classList.add('scrolling-gallery')
                    } else {
                        sessionPageEl?.classList.remove('scrolling-gallery')
                    }
                } else {
                    sessionPageEl?.classList.remove('scrolling-gallery')
                }

                switch (sessionViewModeState.pinKind()) {
                    case 'camera':
                        sessionPageEl?.classList.add('pinned-camera-gallery')
                        break
                    case 'screen':
                        sessionPageEl?.classList.add('pinned-screen-gallery')
                        break
                    default:
                        sessionPageEl?.classList.remove('pinned-camera-gallery')
                        sessionPageEl?.classList.remove('pinned-screen-gallery')
                }
            }
        }

        checkScroll()
        if (fullscreen) {
            sessionPageEl?.classList.add('fullscreen-gallery')
        } else {
            sessionPageEl?.classList.remove('fullscreen-gallery')
        }

        const resizeObserver = new ResizeObserver(checkScroll)
        resizeObserver.observe(container.current)

        return () => {
            resizeObserver.disconnect()
        }
    }, [isSidebarRetracted, numVids, fullscreen])

    return container
}
